import type {
  AttributionContext,
  BroadcastTraffic,
  CampaignPair,
  FlowTraffic,
  SocialTraffic,
  UntaggedTraffic,
} from "@quotientjs/core"
import { z } from "zod"
import {
  QT_BUSINESS_ID_PARAM,
  QT_CAMPAIGN_ID_PARAM,
  QT_EMAIL_ID_PARAM,
  QT_PERSON_ID_PARAM,
  QT_SRC_CONTENT_ID_PARAM,
  QT_SRC_CONTENT_TYPE_PARAM,
  SOCIAL_SOURCES,
  UTM_CAMPAIGN_PARAM,
  UTM_CONTENT_PARAM,
  UTM_MEDIUM_PARAM,
  UTM_SOURCE_PARAM,
} from "./constants"

/**
 * The result of inspecting one URL for managed attribution parameters.
 * `none` preserves the active context, `invalid` ignores a malformed
 * attribution tag, and `replace` provides the complete context that should
 * become active.
 */
export type UrlAttributionResolution =
  | { kind: "none" }
  | { kind: "invalid"; reason: string }
  | {
      kind: "replace"
      context: AttributionContext
      serializedParams: string
    }

const optionalUrlParamSchema = z.preprocess(
  (value) => (value === "" ? undefined : value),
  z.string().optional(),
)

/** Every URL parameter owned by on-site tracking. */
const managedUrlParamsSchema = z.object({
  [QT_BUSINESS_ID_PARAM]: optionalUrlParamSchema,
  [QT_SRC_CONTENT_TYPE_PARAM]: optionalUrlParamSchema,
  [QT_SRC_CONTENT_ID_PARAM]: optionalUrlParamSchema,
  [QT_CAMPAIGN_ID_PARAM]: optionalUrlParamSchema,
  [QT_PERSON_ID_PARAM]: optionalUrlParamSchema,
  [QT_EMAIL_ID_PARAM]: optionalUrlParamSchema,
  [UTM_SOURCE_PARAM]: optionalUrlParamSchema,
  [UTM_MEDIUM_PARAM]: optionalUrlParamSchema,
  [UTM_CAMPAIGN_PARAM]: optionalUrlParamSchema,
  [UTM_CONTENT_PARAM]: optionalUrlParamSchema,
})

type ManagedUrlParams = z.infer<typeof managedUrlParamsSchema>

/** Requires campaign ID and UTM campaign slug together, just utm slug, or not at all. */
const campaignPairUrlSchema = z.union([
  z.object({
    [QT_CAMPAIGN_ID_PARAM]: z.string().min(1),
    [UTM_CAMPAIGN_PARAM]: z.string().min(1),
  }),
  z.object({
    [UTM_CAMPAIGN_PARAM]: z.string().min(1),
  }),
  z.object({
    [QT_CAMPAIGN_ID_PARAM]: z.undefined().optional(),
    [UTM_CAMPAIGN_PARAM]: z.undefined().optional(),
  }),
])

const taggedUrlBaseSchema = z.object({
  [QT_BUSINESS_ID_PARAM]: z.string().min(1),
  [QT_SRC_CONTENT_ID_PARAM]: z.string().min(1),
  [UTM_CONTENT_PARAM]: z.string().min(1),
})

/** Parses the URL form of email-broadcast attribution. */
const broadcastUrlSchema = taggedUrlBaseSchema
  .extend({
    [QT_SRC_CONTENT_TYPE_PARAM]: z.literal("email_broadcast"),
    [QT_PERSON_ID_PARAM]: z.string().min(1),
    [QT_EMAIL_ID_PARAM]: z.string().min(1),
    [UTM_SOURCE_PARAM]: z.literal("qt_email_broadcast"),
    [UTM_MEDIUM_PARAM]: z.literal("email"),
  })
  .and(campaignPairUrlSchema)
  .transform(
    (params): BroadcastTraffic => ({
      srcContentType: "EMAIL_BROADCAST",
      srcContentId: params[QT_SRC_CONTENT_ID_PARAM],
      personId: params[QT_PERSON_ID_PARAM],
      emailId: params[QT_EMAIL_ID_PARAM],
      utmSource: params[UTM_SOURCE_PARAM],
      utmMedium: params[UTM_MEDIUM_PARAM],
      utmContent: params[UTM_CONTENT_PARAM],
      ...campaignContext(params),
    }),
  )

/** Parses the URL form of flow-email attribution. */
const flowUrlSchema = taggedUrlBaseSchema
  .extend({
    [QT_SRC_CONTENT_TYPE_PARAM]: z.literal("flow"),
    [QT_PERSON_ID_PARAM]: z.string().min(1),
    [QT_EMAIL_ID_PARAM]: z.string().min(1),
    [UTM_SOURCE_PARAM]: z.literal("qt_flow"),
    [UTM_MEDIUM_PARAM]: z.literal("email"),
  })
  .and(campaignPairUrlSchema)
  .transform(
    (params): FlowTraffic => ({
      srcContentType: "FLOW",
      srcContentId: params[QT_SRC_CONTENT_ID_PARAM],
      personId: params[QT_PERSON_ID_PARAM],
      emailId: params[QT_EMAIL_ID_PARAM],
      utmSource: params[UTM_SOURCE_PARAM],
      utmMedium: params[UTM_MEDIUM_PARAM],
      utmContent: params[UTM_CONTENT_PARAM],
      ...campaignContext(params),
    }),
  )

/** Parses the URL form of social-post attribution. */
const socialUrlSchema = taggedUrlBaseSchema
  .extend({
    [QT_SRC_CONTENT_TYPE_PARAM]: z.literal("social_post"),
    [QT_PERSON_ID_PARAM]: z.undefined().optional(),
    [QT_EMAIL_ID_PARAM]: z.undefined().optional(),
    [UTM_SOURCE_PARAM]: z.enum(SOCIAL_SOURCES),
    [UTM_MEDIUM_PARAM]: z.literal("social"),
  })
  .and(campaignPairUrlSchema)
  .transform(
    (params): SocialTraffic => ({
      srcContentType: "SOCIAL_POST",
      srcContentId: params[QT_SRC_CONTENT_ID_PARAM],
      utmSource: params[UTM_SOURCE_PARAM],
      utmMedium: params[UTM_MEDIUM_PARAM],
      utmContent: params[UTM_CONTENT_PARAM],
      ...campaignContext(params),
    }),
  )

const taggedUrlSchema = z.union([
  broadcastUrlSchema,
  flowUrlSchema,
  socialUrlSchema,
])

/** Parses non-Quotient UTM parameters without imposing cross-field rules. */
const untaggedUrlSchema = z
  .object({
    [UTM_SOURCE_PARAM]: optionalUrlParamSchema,
    [UTM_MEDIUM_PARAM]: optionalUrlParamSchema,
    [UTM_CAMPAIGN_PARAM]: optionalUrlParamSchema,
    [UTM_CONTENT_PARAM]: optionalUrlParamSchema,
  })
  .transform(
    (params): UntaggedTraffic => ({
      utmSource: params[UTM_SOURCE_PARAM],
      utmMedium: params[UTM_MEDIUM_PARAM],
      utmCampaign: params[UTM_CAMPAIGN_PARAM],
      utmContent: params[UTM_CONTENT_PARAM],
    }),
  )

const QUOTIENT_PARAM_NAMES: readonly [
  typeof QT_BUSINESS_ID_PARAM,
  typeof QT_SRC_CONTENT_TYPE_PARAM,
  typeof QT_SRC_CONTENT_ID_PARAM,
  typeof QT_CAMPAIGN_ID_PARAM,
  typeof QT_PERSON_ID_PARAM,
  typeof QT_EMAIL_ID_PARAM,
] = [
  QT_BUSINESS_ID_PARAM,
  QT_SRC_CONTENT_TYPE_PARAM,
  QT_SRC_CONTENT_ID_PARAM,
  QT_CAMPAIGN_ID_PARAM,
  QT_PERSON_ID_PARAM,
  QT_EMAIL_ID_PARAM,
]

/**
 * Applies the SDK's attribution replacement rules to one URL. A valid managed
 * parameter set replaces session attribution; an incomplete or contradictory
 * Quotient tag is rejected as a group; and a URL without managed parameters
 * leaves the current session attribution untouched.
 */
export function resolveUrlAttribution({
  searchParams,
}: {
  searchParams: URLSearchParams
}): UrlAttributionResolution {
  const params = managedUrlParamsSchema.parse(
    Object.fromEntries(searchParams.entries()),
  )
  const serializedParams = serializeManagedParams({ params })
  if (!serializedParams) {
    return { kind: "none" }
  }

  const hasQuotientParams = QUOTIENT_PARAM_NAMES.some((name) => params[name])
  if (!hasQuotientParams) {
    return {
      kind: "replace",
      context: untaggedUrlSchema.parse(params),
      serializedParams,
    }
  }

  const tagged = taggedUrlSchema.safeParse(params)
  if (!tagged.success) {
    return {
      kind: "invalid",
      reason: "tagged URL is incomplete or contradictory",
    }
  }

  return { kind: "replace", context: tagged.data, serializedParams }
}

function campaignContext({
  [QT_CAMPAIGN_ID_PARAM]: campaignId,
  [UTM_CAMPAIGN_PARAM]: utmCampaign,
}: Pick<
  ManagedUrlParams,
  typeof QT_CAMPAIGN_ID_PARAM | typeof UTM_CAMPAIGN_PARAM
>): CampaignPair {
  if (campaignId && utmCampaign) {
    return { campaignId, utmCampaign }
  }
  if (utmCampaign) {
    return {
      utmCampaign,
    }
  }
  return {}
}

function serializeManagedParams({
  params,
}: {
  params: ManagedUrlParams
}): string {
  const serialized = new URLSearchParams()
  for (const [name, value] of Object.entries(params)) {
    if (value) {
      serialized.set(name, value)
    }
  }
  return serialized.toString()
}
